import { useContext, useState } from 'react';

import { formatDate, noop } from '@ajaibid/utils';
import { API_SUCCESS_CODE } from '@config/constanta';
import { AlertContext } from '@context/alert';
import useLayout from '@context/layout/useLayout';

import stockService from '@services/stock';
import {
  postStockAmend,
  postStockAutoOrder,
  putStockAutoOrderAmend,
} from '@services/api/stock/trading';
import { postDtAmend } from '@services/api/stock/dayTrading';
import {
  postMtAmend,
  postMtAutoOrder,
  putMtAutoOrderAmend,
} from '@services/api/stock/marginTrading';

import { CONST_DT_CUSTOM_ERROR_CODES } from '../../constants';
import { useStockBuySellContext } from '../../StockBuySell/hooks';
// import { useTracker } from '../../tracker';

const makeLimitOrderPayload = ({ code, formState, formKey }) => ({
  ticker_code: code,
  price: formState[formKey.INPUT_PRICE],
  lot: formState[formKey.INPUT_LOT_COUNT],
  idxno: formState[formKey.CONST_IDX_ORDER_ID],
});

const makeAutoOrderPayload = ({ code, formState, formKey, copied_from_id = null }) => ({
  copied_from_id,
  code,
  side: formState[formKey.INPUT_ORDER_SIDE],
  order_price: formState[formKey.INPUT_PRICE],
  lot: formState[formKey.INPUT_LOT_COUNT],
  expiry: 'day',
  start_date: formatDate(formState[formKey.INPUT_START_DATE], 'YYYY-MM-DD'),
  end_date: formatDate(formState[formKey.INPUT_END_DATE], 'YYYY-MM-DD'),
  criterion: 'PRICE',
  comparator: formState[formKey.INPUT_THRESHOLD_COMPARATOR]?.value,
  value: formState[formKey.INPUT_THRESHOLD_VALUE],
});

function useSubmitAmend(code, { onSuccess = noop, onDtError } = {}) {
  const layout = useLayout();
  const { showAlert } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);

  const {
    formState,
    formKey,
    isAmendExpired,
    isBuy,
    isSell,
    isAccountRegular,
    isAccountDT,
    isAccountMT,
    isLimitOrder,
    isAutoOrder,
  } = useStockBuySellContext();

  const { isTradeable: isDtTradeable } = stockService.detail.dt.tradeable.useDtTradeable(code);
  const { isTradeable: isMtTradeable } = stockService.detail.mt.tradeable.useMtTradeable(code);
  const { refetch: refetchTradelimit } = stockService.account.regular.tradelimit.useTradelimit();
  const { refetch: refetchDtTradelimit } = stockService.account.dt.tradelimit.useDtTradelimit({
    code,
    enabled: isDtTradeable, // Only call API when stock is DT tradeable
  });
  const { refetch: refetchMtTradelimit } = stockService.account.mt.tradelimit.useMtTradelimit({
    code,
    enabled: isMtTradeable, // Only call API when stock is MT tradeable
  });
  const { refetch: refetchPortfolio } =
    stockService.account.regular.portfolio.usePortfolioByCode(code);
  const { refetch: refetchDtPortfolio } =
    stockService.account.dt.portfolio.useDtPortfolioByCode(code);
  const { refetch: refetchMtPortfolio } =
    stockService.account.mt.portfolio.useMtPortfolioByCode(code);

  // const { eventOrderSendFail, eventOrderSendSuccess } = useTracker();

  const makeApiCall = async (post, postParams, funcParams = {}) => {
    const {
      errorCodeKey = 'err_code',
      errorMessageKey = 'err_message',
      successMessage = 'Berhasil mengubah order',
      defaultErrorMessage = 'Gagal mengubah order',
      refetchData = () => {},
    } = funcParams;

    setIsLoading(true);
    const response = await post(...postParams);

    const isSuccess = response?.[errorCodeKey] === API_SUCCESS_CODE;
    const isDtError = CONST_DT_CUSTOM_ERROR_CODES.includes(response?.[errorCodeKey]);
    switch (true) {
      case isSuccess:
        // For success, show success message as an alert
        // eventOrderSendSuccess();
        showAlert(successMessage, 'success');
        refetchData();
        onSuccess();
        break;
      case isDtError:
        // For DT-specific error, handle differently
        // eventOrderSendFail();
        refetchData();
        onDtError(response?.[errorCodeKey]);
        break;
      default:
        // For other error, show error message as an alert
        // eventOrderSendFail();
        showAlert(response?.[errorMessageKey] || defaultErrorMessage, 'error');
        refetchData();
        break;
    }
    setIsLoading(false);

    // Refetch data on order open & order history
    layout.bottomPanel.refetchTable();
  };

  const submitAmend = () => {
    switch (true) {
      case isLimitOrder && isAccountRegular: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postStockAmend, [payload], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'statusMessage',
          refetchData: () => {
            refetchTradelimit();
            refetchPortfolio();
          },
        });
        break;
      }
      case isLimitOrder && isAccountDT: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postDtAmend, [payload], {
          refetchData: () => {
            refetchDtTradelimit();
            refetchDtPortfolio();
          },
        });
        break;
      }
      case isLimitOrder && isAccountMT: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postMtAmend, [payload], {
          refetchData: () => {
            refetchMtTradelimit();
            refetchMtPortfolio();
          },
        });
        break;
      }
      case isAutoOrder && isAccountRegular && !isAmendExpired: {
        const order_id = formState[formKey.CONST_ORDER_ID];
        const payload = makeAutoOrderPayload({ code, formState, formKey });
        makeApiCall(putStockAutoOrderAmend, [order_id, payload], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          refetchData: () => {
            refetchTradelimit();
            refetchPortfolio();
          },
        });
        break;
      }
      case isAutoOrder && isAccountMT && !isAmendExpired: {
        const order_id = formState[formKey.CONST_ORDER_ID];
        const payload = makeAutoOrderPayload({ code, formState, formKey });
        makeApiCall(putMtAutoOrderAmend, [order_id, payload], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          refetchData: () => {
            refetchMtTradelimit();
            refetchMtPortfolio();
          },
        });
        break;
      }
      case isAutoOrder && isAccountRegular && isAmendExpired: {
        const _autoOrder = (isBuy && 'Auto Order Buy') || (isSell && 'Auto Order Sell') || '';
        const order_id = formState[formKey.CONST_ORDER_ID];
        const payload = makeAutoOrderPayload({
          code,
          formState,
          formKey,
          copied_from_id: order_id,
        });
        makeApiCall(postStockAutoOrder, [payload], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          successMessage: `${_autoOrder} ${code} telah aktif`,
          defaultErrorMessage: `Gagal mengaktifkan ${_autoOrder} ${code}`,
          refetchData: () => {
            refetchTradelimit();
            refetchPortfolio();
          },
        });
        break;
      }
      case isAutoOrder && isAccountMT && isAmendExpired: {
        const _autoOrder = (isBuy && 'Auto Order Buy') || (isSell && 'Auto Order Sell') || '';
        const order_id = formState[formKey.CONST_ORDER_ID];
        const payload = makeAutoOrderPayload({
          code,
          formState,
          formKey,
          copied_from_id: order_id,
        });
        makeApiCall(postMtAutoOrder, [payload], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          successMessage: `${_autoOrder} ${code} telah aktif`,
          defaultErrorMessage: `Gagal mengaktifkan ${_autoOrder} ${code}`,
          refetchData: () => {
            refetchMtTradelimit();
            refetchMtPortfolio();
          },
        });
        break;
      }
      default:
        break;
    }
  };

  return { isLoading, submitAmend };
}

export default useSubmitAmend;
